/**
 * BasicProCompareSheet: the Basic vs Pro table for someone who has not
 * signed up yet (owner ask, 2026-10-01).
 *
 * WHY IT EXISTS
 *   New trainers were reading the sign-up screen as the start of a payment
 *   flow. The sign-up page now says Basic is free, and this sheet shows the
 *   whole shape of it: the same table the Why go Pro sheet and Settings draw
 *   (ProCompareTable, from lib/proFeatureRows), so the three never disagree.
 *
 *   Read-only on purpose. There is no Upgrade button: there is no account to
 *   upgrade yet, and the point here is reassurance, not a pitch.
 */
import { X } from "lucide-react";
import { ProCompareTable } from "@/components/ProCompareTable";

export function BasicProCompareSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Basic and Pro compared"
      data-testid="basic-pro-sheet"
      className="fixed inset-0 z-[210] flex items-end md:items-center justify-center px-3 bg-black/75 backdrop-blur-sm anim-fade-up"
      style={{
        paddingTop: "max(0.75rem, var(--nx-safe-top))",
        paddingBottom: "max(0.75rem, var(--nx-safe-bottom))",
      }}
      onClick={onClose}
    >
      {/* The card is the scroller (see PaywallModal, 2026-09-11). */}
      <div
        className="relative w-full max-w-md v2-neon-card anim-fade-scale max-h-full overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="basic-pro-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10"
          style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)" }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-5 pt-6">
          <div className="flex justify-center mb-2.5">
            <span className="v2-eyebrow">
              <span className="v2-eyebrow__dot" />
              BASIC VS PRO
            </span>
          </div>
          <h2 className="v2-display text-center text-[22px] leading-tight" style={{ color: "var(--text-primary)" }}>
            Basic is free. Pro is optional.
          </h2>
          <p className="text-center text-[12px] mt-1.5 leading-relaxed px-2" style={{ color: "var(--text-secondary)" }}>
            Signing up costs nothing. Everything with a tick under Basic is yours, free, for as long as you like.
          </p>

          <ProCompareTable testIdPrefix="basic-pro" />

          <button
            type="button"
            onClick={onClose}
            data-testid="basic-pro-done"
            className="mt-4 w-full rounded-full py-2.5 text-sm font-bold transition-transform active:scale-95"
            style={{
              background: "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
              color: "var(--text-on-accent)",
              boxShadow: "0 0 10px var(--accent-fill-glow)",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
